"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

export function SiteSearchBox({
  className,
  prefix = "",
}: {
  className?: string;

  prefix?: string;
}) {
  const t = useTranslations("site.common");
  const router = useRouter();
  const params = useSearchParams();
  const initial = params.get("q") ?? "";
  const [value, setValue] = useState(initial);

  const [open, setOpen] = useState(Boolean(initial));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  function submit(event: React.FormEvent) {
    event.preventDefault();

    const keyword = value.trim();

    if (!keyword) {

      setOpen(true);
      return;
    }

    router.push(`${prefix}/search/?q=${encodeURIComponent(keyword)}`);
  }

  return (
    <form
      role="search"
      onSubmit={submit}
      className={cn("relative flex items-center", className)}
    >
      <button
        type="submit"
        aria-label={t("search")}
        aria-expanded={open}
        onClick={() => {
          if (!open) {
            setOpen(true);
          }
        }}
        className={cn(
          "z-10 flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground",
          open && "absolute left-0 size-9",
        )}
      >
        <Search aria-hidden className="size-[18px]" />
      </button>
      <input
        ref={inputRef}
        type="search"
        name="q"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => {
          if (!value.trim()) {
            setOpen(false);
          }
        }}
        placeholder={t("search")}
        aria-label={t("siteSearch")}

        className={cn(
          "h-9 rounded-full border bg-background/60 text-sm outline-none transition-[width,opacity,padding] duration-300 placeholder:text-muted-foreground focus:border-foreground/30",
          open
            ? "w-44 border-border pr-3 pl-9 opacity-100 sm:w-56"
            : "pointer-events-none w-0 border-transparent px-0 opacity-0",
        )}
      />
    </form>
  );
}
